import axios from 'axios';
import { put, call } from 'redux-saga/effects';

// LOCALE
import { t } from '@/locale/i18n';

// CONSTANTS
import * as endpoints from '@/constants/api';

// UTILITY
import getErrorMessage from '@/utility/errors';
import store from '@/utility/store';
import { getLink } from '@/utility/routes';
import { analyticsAuth } from '@/utility/analytics';
import { showErrorNotification } from '@/utility/saga';
import { pushSuccessNotification } from '@/utility/snackbar';

// STORE
import { AuthActions } from '@/store/actions';
import { setAuthorizationToken, deleteAuthorizationToken } from '@/store/configDefaultAPI';

import { getAppDictionaryAction } from '@/store/slices/app.slice';

const {
  login,
  loginAs,
  loginWithAuth0,
  registerUser,
  createPassword,
  resetPasswordSendEmail,
  resetPassUpdate,
  logout,
} = AuthActions;

function* setLocalSaga(token, tokenType, adminToken) {
  if (adminToken) {
    yield store.set('admin_token', adminToken);
  }

  yield store.set('token', token);
  yield store.set('token_type', tokenType);
}

function* setAuth(data, adminToken) {
  yield call(setAuthorizationToken, data.token, data.token_type);
  yield call(setLocalSaga, data.token, data.token_type, adminToken);
}

export function* logoutSaga() {
  yield store.remove('token');
  yield store.remove('admin_token');
  yield store.remove('token_type');

  yield call(deleteAuthorizationToken);
}

export function* getMe() {
  yield put({ type: login.types.START });
  const token = yield store.get('token');

  if (token) {
    try {
      const response = yield axios.get(endpoints.AUTH_GET_USER);

      if (response) {
        const { data: respData } = response;

        analyticsAuth({ email: respData.user.email, user_id: respData.user.id, ...respData });

        yield put({
          type: login.types.SUCCESS,
          payload: {
            user: respData.user,
            partner: respData.partner || {},
          },
        });
      }
    } catch (error) {
      yield call(showErrorNotification, error);

      yield call(logoutSaga);

      yield put({
        type: login.types.FAIL,
      });
    }
  }
}

export function* authCheckLocalStorageSaga() {
  const token = yield store.get('token');
  const tokenType = yield store.get('token_type');

  if (token) {
    try {
      yield call(setAuthorizationToken, token, tokenType);
      yield call(getMe);
      yield put(getAppDictionaryAction());
    } catch (error) {
      yield call(showErrorNotification, error);

      yield put({ type: logout.types.INIT });
    }
  }
}

export function* authSaga(action) {
  yield put({ type: login.types.START });

  const { data } = action.payload;
  const url = endpoints.AUTH_LOGIN;

  try {
    const { data: respData } = yield axios.post(url, data);

    yield call(setAuth, respData);

    yield put(getAppDictionaryAction());

    analyticsAuth({ email: data.email, user_id: respData.user.id, ...respData });

    yield put({
      type: login.types.SUCCESS,
      payload: {
        user: respData.user,
        partner: respData.partner || {},
      },
    });
  } catch (error) {
    const errorStatus = error.response?.status;

    if (errorStatus !== 423) {
      yield call(showErrorNotification, error);
    }

    yield put({
      type: login.types.FAIL,
    });
  }
}

export function* authAsSaga(action) {
  yield put({ type: loginAs.types.START });

  const { user_id, variant } = action.payload;

  const admin_token = store.get('admin_token');
  const token = store.get('token');
  const token_type = store.get('token_type');

  try {
    if (variant === 'admin' && admin_token) {
      yield call(setAuth, { token: admin_token, token_type });

      store.remove('admin_token');
    } else if (variant === 'user' && user_id) {
      const url = getLink(endpoints.AUTH_LOGIN_AS_USER, { user_id });

      const { data: respData } = yield axios.get(url);

      yield call(setAuth, respData, token);
    } else {
      throw new Error(`Can't login as ${variant}`);
    }

    yield put(getAppDictionaryAction());

    // NOT HANDLED IN REDUCER (ACTIVE LOADER BEFORE RELOAD)
    yield put({ type: loginAs.types.SUCCESS });

    window.location.pathname = '/';
  } catch (error) {
    yield call(showErrorNotification, error);

    yield put({
      type: loginAs.types.FAIL,
    });
  }
}

export function* authWithAuth0Saga(action) {
  yield put({ type: loginWithAuth0.types.START });

  const { data } = action.payload;
  const url = endpoints.AUTH_AUTH0_CALLBACK;

  try {
    const { data: respData } = yield axios.get(`${url}${data.auth0SearchParams}`);

    yield call(setAuth, respData);

    yield put(getAppDictionaryAction());

    yield put({
      type: loginWithAuth0.types.SUCCESS,
      payload: {
        user: respData.user,
        partner: respData.partner || {},
      },
    });
  } catch (error) {
    yield call(showErrorNotification, error);

    yield put({
      type: loginWithAuth0.types.FAIL,
    });
  }
}

export function* registerSaga(action) {
  yield put({ type: registerUser.types.START });

  const { data } = action.payload;
  const url = endpoints.AUTH_REGISTER;

  try {
    const { data: respData } = yield axios.post(url, data);

    yield call(setAuth, respData);

    yield put(getAppDictionaryAction());

    analyticsAuth({ email: data.email, user_id: respData.user.id, ...respData });

    if (!respData.register) {
      pushSuccessNotification(t('notification.autoLogin'));
    }

    yield put({
      type: registerUser.types.SUCCESS,
      payload: respData,
    });
  } catch (error) {
    yield call(showErrorNotification, error);

    yield put({
      type: registerUser.types.FAIL,
      payload: { errorMessage: getErrorMessage(error) },
    });
  }
}

export function* resetPassSendEmailSaga(action) {
  yield put({ type: resetPasswordSendEmail.types.START });

  const { email } = action.payload;
  const url = endpoints.AUTH_RP_SEND_EMAIL;

  try {
    yield axios.post(url, { email });

    pushSuccessNotification(t('notification.resetPassSuccess'), { autoHideDuration: undefined });

    yield put({
      type: resetPasswordSendEmail.types.SUCCESS,
    });
  } catch (error) {
    yield call(showErrorNotification, error);

    yield put({
      type: resetPasswordSendEmail.types.FAIL,
    });
  }
}

export function* resetPassUpdateSaga(action) {
  yield put({ type: resetPassUpdate.types.START });

  const { data } = action.payload;
  const url = endpoints.AUTH_RP_UPDATE;

  try {
    const { data: respData } = yield axios.post(url, data);

    yield call(setAuth, respData);

    yield put(getAppDictionaryAction());

    analyticsAuth({ email: respData.user.email, user_id: respData.user.id, ...respData });

    pushSuccessNotification(t('notification.resetPassUpdateSuccess'));

    yield put({
      type: resetPassUpdate.types.SUCCESS,
      payload: {
        user: respData.user,
        partner: respData.partner || {},
      },
    });
  } catch (error) {
    yield call(showErrorNotification, error);

    yield put({
      type: resetPassUpdate.types.FAIL,
    });
  }
}

export function* createPasswordSaga(action) {
  yield put({ type: createPassword.types.START });

  const { data } = action.payload;
  const url = endpoints.AUTH_CREATE_PASSWORD;

  try {
    const { data: respData } = yield axios.post(url, data);

    yield call(setAuth, respData);

    yield put(getAppDictionaryAction());

    analyticsAuth({ email: respData.user.email, user_id: respData.user.id, ...respData });

    yield put({
      type: createPassword.types.SUCCESS,
      payload: {
        user: respData.user,
        partner: respData.partner || {},
      },
    });
  } catch (error) {
    yield call(showErrorNotification, error);

    yield put({
      type: createPassword.types.FAIL,
    });
  }
}

export default authSaga;
